'use client';
import React, { useEffect, useRef, useState, useCallback } from 'react';
import AppImage from '@/components/ui/AppImage';
import type { TattooStyle } from '@/lib/content-types';
import { useLanguage } from '@/components/language/LanguageProvider';
import { translateStyleLabel, translateStyleTag } from '@/lib/i18n';

interface LightboxProps {
  category: TattooStyle;
  onClose: () => void;
}

function Lightbox({ category, onClose }: LightboxProps) {
  const { dictionary } = useLanguage();

  const [activeIdx, setActiveIdx] = useState(0);
  const scrollRef = useRef<HTMLDivElement>(null);
  const overlayRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      if (e.key === 'ArrowRight') setActiveIdx((i) => Math.min(i + 1, category.images.length - 1));
      if (e.key === 'ArrowLeft') setActiveIdx((i) => Math.max(i - 1, 0));
    };
    document.addEventListener('keydown', handleKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleKey);
      document.body.style.overflow = '';
    };
  }, [onClose, category.images.length]);

  useEffect(() => {
    const el = scrollRef.current?.children[activeIdx] as HTMLElement | undefined;
    el?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
  }, [activeIdx]);

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) onClose();
    },
    [onClose]
  );

  return (
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      className="fixed inset-0 z-[100] flex flex-col items-center justify-center bg-black/92 backdrop-blur-md"
      style={{ animation: 'fadeInScale 0.3s cubic-bezier(0.16,1,0.3,1) forwards' }}
    >
      <button
        onClick={onClose}
        className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center rounded-full glass-dark text-foreground/70 hover:text-foreground hover:border-primary/40 transition-all duration-200"
        aria-label={dictionary.portfolio.close}
      >
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
        >
          <path d="M18 6 6 18M6 6l12 12" />
        </svg>
      </button>

      <div className="absolute top-5 left-1/2 -translate-x-1/2 flex flex-col items-center gap-1 pointer-events-none">
        <span className="text-[10px] font-bold tracking-[0.2em] uppercase text-primary/80 font-mono">
          {category.tag}
        </span>
        <span className="text-foreground/50 text-xs">
          {activeIdx + 1} / {category.images.length}
        </span>
      </div>

      <div className="relative w-full max-w-3xl px-4 flex-1 flex items-center justify-center min-h-0 py-20">
        {activeIdx > 0 && (
          <button
            onClick={() => setActiveIdx((i) => i - 1)}
            className="absolute left-2 sm:left-6 z-10 w-10 h-10 flex items-center justify-center rounded-full glass-dark text-foreground/70 hover:text-foreground hover:border-primary/40 transition-all duration-200"
            aria-label={dictionary.portfolio.previous}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
        )}
        {activeIdx < category.images.length - 1 && (
          <button
            onClick={() => setActiveIdx((i) => i + 1)}
            className="absolute right-2 sm:right-6 z-10 w-10 h-10 flex items-center justify-center rounded-full glass-dark text-foreground/70 hover:text-foreground hover:border-primary/40 transition-all duration-200"
            aria-label={dictionary.portfolio.next}
          >
            <svg
              width="16"
              height="16"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2.5"
              strokeLinecap="round"
            >
              <path d="M9 18l6-6-6-6" />
            </svg>
          </button>
        )}

        <div
          key={activeIdx}
          className="relative w-full max-h-[65vh] aspect-[3/4] max-w-sm mx-auto rounded-2xl overflow-hidden shadow-2xl border border-white/10"
          style={{ animation: 'fadeInScale 0.35s cubic-bezier(0.16,1,0.3,1) forwards' }}
        >
          <AppImage
            src={category.images[activeIdx].url}
            alt={category.images[activeIdx].alt}
            fill
            className="object-cover"
            sizes="(max-width: 768px) 90vw, 480px"
          />
        </div>
      </div>

      <div className="w-full max-w-3xl px-4 pb-6 flex-shrink-0">
        <div
          ref={scrollRef}
          className="flex gap-2 sm:gap-3 overflow-x-auto pb-2 justify-start sm:justify-center"
          style={{ scrollbarWidth: 'none' }}
        >
          {category.images.map((img, i) => (
            <button
              key={i}
              onClick={() => setActiveIdx(i)}
              className={`relative flex-shrink-0 w-16 h-20 rounded-xl overflow-hidden border-2 transition-all duration-300 ${
                i === activeIdx
                  ? 'border-primary shadow-[0_0_12px_rgba(200,169,110,0.4)] scale-105'
                  : 'border-white/10 opacity-50 hover:opacity-80 hover:border-white/25'
              }`}
              aria-label={`${dictionary.portfolio.viewPhoto} ${i + 1}`}
            >
              <AppImage src={img.url} alt={img.alt} fill className="object-cover" sizes="64px" />
            </button>
          ))}
        </div>
      </div>
    </div>
  );
}

function useScrollReveal() {
  const refs = useRef<(HTMLElement | null)[]>([]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.08, rootMargin: '0px 0px -40px 0px' }
    );
    refs.current.forEach((el) => {
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);

  return refs;
}

interface PortfolioSectionProps {
  styles: TattooStyle[];
}

export default function PortfolioSection({ styles }: PortfolioSectionProps) {
  const revealRefs = useScrollReveal();
  const [openCategory, setOpenCategory] = useState<TattooStyle | null>(null);
  const { dictionary, language } = useLanguage();

  return (
    <>
      {openCategory && <Lightbox category={openCategory} onClose={() => setOpenCategory(null)} />}

      <section id="portfolio" className="relative bg-[#0D0D0D] py-20 md:py-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-primary/5 rounded-full blur-[120px] pointer-events-none" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/4 rounded-full blur-[100px] pointer-events-none" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Section header */}
          <div
            ref={(el) => {
              revealRefs.current[0] = el;
            }}
            className="reveal flex flex-col md:flex-row md:items-end justify-between mb-12 gap-6 max-w-5xl mx-auto"
          >
            <div>
              <span className="section-label">{dictionary.portfolio.sectionLabel}</span>
              <h2
                className="text-4xl md:text-5xl font-black text-foreground tracking-tight leading-[1.05]"
                style={{ fontFamily: 'var(--font-fraunces)' }}
              >
                {dictionary.portfolio.titleLine1}
                <br />
                <span className="text-gradient-gold italic">{dictionary.portfolio.titleLine2}</span>
              </h2>
            </div>
          </div>

          {/* Single-row style cards */}
          <div
            ref={(el) => {
              revealRefs.current[1] = el;
            }}
            className="reveal reveal-delay-1 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 mb-6 max-w-5xl mx-auto"
          >
            {styles.map((cat) => (
              <button
                key={cat.id}
                onClick={() => {
                  if (cat.images.length) setOpenCategory(cat);
                }}
                className="group relative aspect-[3/4] rounded-2xl overflow-hidden border border-white/[0.07] hover:border-primary/30 transition-all duration-500 hover:-translate-y-1.5 hover:shadow-[0_20px_50px_rgba(0,0,0,0.6),0_0_30px_rgba(200,169,110,0.1)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/60 disabled:opacity-50 disabled:hover:translate-y-0"
                disabled={!cat.images.length}
                aria-label={`${dictionary.portfolio.viewGallery} ${translateStyleLabel(cat.label, language)}`}
              >
                <AppImage
                  src={cat.cover}
                  alt={cat.coverAlt}
                  fill
                  className="object-cover transition-transform duration-700 group-hover:scale-105"
                  sizes="(max-width: 640px) 50vw, (max-width: 1024px) 33vw, 20vw"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/85 via-black/20 to-transparent transition-opacity duration-500 group-hover:opacity-95" />
                <div className="absolute bottom-0 left-0 right-0 p-3 translate-y-1 group-hover:translate-y-0 transition-transform duration-300">
                  <span className="block text-[8px] font-bold tracking-[0.18em] uppercase text-primary/70 font-mono mb-0.5">
                    {translateStyleTag(cat.tag, language)}
                  </span>
                  <div className="flex items-center justify-between gap-1">
                    <span
                      className="text-foreground font-bold text-sm leading-tight"
                      style={{ fontFamily: 'var(--font-fraunces)' }}
                    >
                      {translateStyleLabel(cat.label, language)}
                    </span>
                    <span className="w-6 h-6 rounded-full bg-primary/20 border border-primary/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-all duration-300 flex-shrink-0">
                      <svg
                        width="9"
                        height="9"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2.5"
                        strokeLinecap="round"
                        className="text-primary"
                      >
                        <path d="M5 12h14M12 5l7 7-7 7" />
                      </svg>
                    </span>
                  </div>
                </div>
              </button>
            ))}
          </div>
          {!styles.length && (
            <p className="text-center text-sm text-muted-foreground py-6 border border-border rounded-2xl">
              {dictionary.portfolio.empty}
            </p>
          )}

          {/* Compact Instagram link row */}
          <div
            ref={(el) => {
              revealRefs.current[2] = el;
            }}
            className="reveal reveal-delay-2 flex flex-wrap items-center justify-center gap-2 sm:gap-3 mb-2 text-center max-w-5xl mx-auto"
          >
            <svg
              width="14"
              height="14"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="1.5"
              strokeLinecap="round"
              strokeLinejoin="round"
              className="text-primary/60 flex-shrink-0"
            >
              <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
              <circle cx="12" cy="12" r="4" />
              <circle cx="17.5" cy="6.5" r="1" fill="currentColor" stroke="none" />
            </svg>
            <span className="text-muted-foreground/60 text-xs">{dictionary.portfolio.seeMore}</span>
            <a
              href="https://www.instagram.com/juga_tattoo/"
              target="_blank"
              rel="noopener noreferrer"
              className="group flex items-center gap-1.5 text-primary text-xs font-semibold hover:text-primary/80 transition-colors duration-200"
              aria-label={dictionary.portfolio.instagramAria}
            >
              @juga_tattoo
              <svg
                width="10"
                height="10"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2.5"
                strokeLinecap="round"
                className="opacity-60 group-hover:opacity-100 group-hover:translate-x-0.5 transition-all duration-200"
              >
                <path d="M5 12h14M12 5l7 7-7 7" />
              </svg>
            </a>
          </div>

          {/* Divider line */}
          <div
            ref={(el) => {
              revealRefs.current[3] = el;
            }}
            className="reveal mt-8 flex items-center gap-4 max-w-5xl mx-auto"
          >
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
            <span className="text-muted-foreground/40 text-[10px] tracking-[0.2em] uppercase font-mono">
              {dictionary.portfolio.divider}
            </span>
            <div className="flex-1 h-px bg-gradient-to-r from-transparent via-white/10 to-transparent" />
          </div>
        </div>
      </section>
    </>
  );
}
